import React, { useState, useEffect } from 'react';
import { Plus, Edit, Trash2, Tag, Calendar, Loader2 } from 'lucide-react';
import { PageHeader } from '@/components/shared/PageHeader';
import { ConfirmDialog } from '@/components/shared/ConfirmDialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import {
  createOffer,
  getAllOffers,
  deleteOffer,
  updateOffer,
} from '@/services/exclusiveOffersApis';
import { getDropDownFirms } from '@/services/newsApi'; // or propFirmApi

export const ExclusiveOffersPage = () => {
  const [offers, setOffers] = useState([]);
  const [firms, setFirms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [editingOffer, setEditingOffer] = useState(null);
  const [deleteOfferItem, setDeleteOfferItem] = useState(null);
  const [isAdding, setIsAdding] = useState(false);

  // Form state
  const [title, setTitle] = useState('');
  const [firmId, setFirmId] = useState('');
  const [discount, setDiscount] = useState('');
  const [code, setCode] = useState('');
  const [description, setDescription] = useState('');
  const [expiryDate, setExpiryDate] = useState('');
  const [isActive, setIsActive] = useState(true);

  // ==================== FETCH ====================
  const fetchOffers = async () => {
    try {
      setLoading(true);
      const data = await getAllOffers();
      setOffers(Array.isArray(data) ? data : data?.data || []);
    } catch (error) {
      console.error(error);
      toast.error('Failed to load offers');
    } finally {
      setLoading(false);
    }
  };

  const fetchFirms = async () => {
    try {
      const data = await getDropDownFirms();
      setFirms(Array.isArray(data) ? data : data?.data || []);
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    fetchOffers();
    fetchFirms();
  }, []);

  // ==================== FORM HELPERS ====================
  const resetForm = () => {
    setTitle('');
    setFirmId('');
    setDiscount('');
    setCode('');
    setDescription('');
    setExpiryDate('');
    setIsActive(true);
  };

  const handleAdd = () => {
    resetForm();
    setIsAdding(true);
    setEditingOffer(null);
  };

  const handleEdit = (offer) => {
    setEditingOffer(offer);
    setIsAdding(false);
    setTitle(offer.title || '');
    setFirmId(offer.firm_id || offer.firmId || '');
    setDiscount(offer.discount || '');
    setCode(offer.code || '');
    setDescription(offer.description || '');
    setExpiryDate(offer.expiry_date || offer.expiryDate || '');
    setIsActive(offer.is_active ?? offer.active ?? true);
  };

  const closeDialog = () => {
    setIsAdding(false);
    setEditingOffer(null);
    resetForm();
  };

  // ==================== BUILD PAYLOAD ====================
  const buildPayload = (includeId = false) => {
  const payload = {
    firm_id: firmId,
    title,
    discount,
    code,
    description,
    expiry_date: expiryDate || null,
    is_active: isActive,
  };

  if (includeId && editingOffer?.id) {
    payload.id = editingOffer.id;
  }

  return payload;
};

// ==================== SAVE ====================
const handleSave = async () => {
  if (!title.trim()) {
    toast.error('Title is required');
    return;
  }
  if (!firmId) {
    toast.error('Please select a prop firm');
    return;
  }
  if (!discount.trim()) {
    toast.error('Discount is required');
    return;
  }
  if (!code.trim()) {
    toast.error('Code is required');
    return;
  }

  setSaving(true);

  try {
    if (isAdding) {
      await createOffer(buildPayload(false));
      toast.success('Offer created successfully');
    } else {
      // { id, firm_id, title, discount, code, description, expiry_date, is_active }
      await updateOffer(buildPayload(true));
      toast.success('Offer updated successfully');
    }

    closeDialog();
    fetchOffers();
  } catch (error) {
    console.error(error);
    toast.error(error?.response?.data?.message || 'Failed to save offer');
  } finally {
    setSaving(false);
  }
};
  // ==================== DELETE ====================
  const handleDelete = async () => {
    if (!deleteOfferItem) return;

    try {
      await deleteOffer(deleteOfferItem.id);
      toast.success('Offer deleted successfully');
      setDeleteOfferItem(null);
      fetchOffers();
    } catch (error) {
      console.error(error);
      toast.error('Failed to delete offer');
    }
  };

  // Helper to get firm name
  const getFirmName = (offer) => {
    const id = offer.firm_id || offer.firmId;
    const firm = firms.find((f) => f.id === id);
    return firm?.name || offer.firm || 'Unknown';
  };

  return (
    <div>
      <PageHeader
        title="Exclusive Offers"
        description="Create and manage special offers and coupon codes"
        breadcrumbs={['Admin', 'Content', 'Exclusive Offers']}
      />

      <Card className="rounded-xl border-slate-200 shadow-sm">
        <CardContent className="p-6">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h3 className="text-lg font-semibold">All Offers</h3>
              <p className="text-sm text-slate-600">{offers.length} total offers</p>
            </div>
            <Button onClick={handleAdd}>
              <Plus className="h-4 w-4 mr-2" />
              Add Offer
            </Button>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
            </div>
          ) : (
            <>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>OFFER</TableHead>
                    <TableHead>PROP FIRM</TableHead>
                    <TableHead>DISCOUNT</TableHead>
                    <TableHead>CODE</TableHead>
                    <TableHead>EXPIRY</TableHead>
                    <TableHead>STATUS</TableHead>
                    <TableHead></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {offers.map((offer) => (
                    <TableRow key={offer.id}>
                      <TableCell>
                        <div>
                          <p className="font-medium text-slate-900">{offer.title}</p>
                          <p className="text-xs text-slate-500">{offer.description}</p>
                        </div>
                      </TableCell>
                      <TableCell>{getFirmName(offer)}</TableCell>
                      <TableCell>
                        <Badge
                          variant="secondary"
                          className="bg-emerald-50 text-emerald-700"
                        >
                          {offer.discount}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <code className="text-xs bg-slate-100 px-2 py-1 rounded">
                          {offer.code}
                        </code>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1 text-sm">
                          <Calendar className="h-3 w-3" />
                          {offer.expiry_date || offer.expiryDate || 'No expiry'}
                        </div>
                      </TableCell>
                      <TableCell>
                        {(offer.is_active ?? offer.active) ? (
                          <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200">
                            Active
                          </Badge>
                        ) : (
                          <Badge
                            variant="secondary"
                            className="bg-slate-100 text-slate-600"
                          >
                            Inactive
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-1">
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8"
                            onClick={() => handleEdit(offer)}
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 text-red-600"
                            onClick={() => setDeleteOfferItem(offer)}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>

              {offers.length === 0 && (
                <div className="text-center py-12 text-slate-500">
                  <Tag className="h-12 w-12 mx-auto mb-3 text-slate-300" />
                  <p>No offers created yet</p>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* Add/Edit Dialog */}
      <Dialog
        open={isAdding || !!editingOffer}
        onOpenChange={(open) => {
          if (!open) closeDialog();
        }}
      >
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>
              {editingOffer ? 'Edit Offer' : 'Create New Offer'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <Label>Offer Title</Label>
              <Input
                placeholder="e.g., New Year Special"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                className="mt-2"
              />
            </div>

            <div>
              <Label>Prop Firm</Label>
              <Select value={firmId} onValueChange={setFirmId}>
                <SelectTrigger className="mt-2">
                  <SelectValue placeholder="Select a prop firm" />
                </SelectTrigger>
                <SelectContent>
                  {firms.map((firm) => (
                    <SelectItem key={firm.id} value={firm.id}>
                      {firm.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Discount</Label>
                <Input
                  placeholder="e.g., 25% OFF, $100 OFF"
                  value={discount}
                  onChange={(e) => setDiscount(e.target.value)}
                  className="mt-2"
                />
              </div>
              <div>
                <Label>Code</Label>
                <Input
                  placeholder="e.g., SAVE25"
                  value={code}
                  onChange={(e) => setCode(e.target.value.toUpperCase())}
                  className="mt-2"
                />
              </div>
            </div>

            <div>
              <Label>Description</Label>
              <Textarea
                placeholder="Brief description of the offer"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                className="mt-2"
                rows={2}
              />
            </div>

            <div>
              <Label>Expiry Date (Optional)</Label>
              <Input
                type="date"
                value={expiryDate}
                onChange={(e) => setExpiryDate(e.target.value)}
                className="mt-2"
              />
            </div>

            <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
              <div>
                <p className="font-medium text-slate-900">Active</p>
                <p className="text-xs text-slate-500">
                  Show this offer on the website
                </p>
              </div>
              <Switch checked={isActive} onCheckedChange={setIsActive} />
            </div>

            <div className="flex gap-2">
              <Button onClick={handleSave} className="flex-1" disabled={saving}>
                {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                <Tag className="h-4 w-4 mr-2" />
                {editingOffer ? 'Update Offer' : 'Create Offer'}
              </Button>
              <Button variant="outline" onClick={closeDialog}>
                Cancel
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <ConfirmDialog
        open={!!deleteOfferItem}
        onOpenChange={() => setDeleteOfferItem(null)}
        title="Delete Offer"
        description={`Are you sure you want to delete "${deleteOfferItem?.title}"? This action cannot be undone.`}
        confirmLabel="Delete"
        variant="destructive"
        onConfirm={handleDelete}
      />
    </div>
  );
};